import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { DOCUMENT, Inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { CelumPropertiesProvider } from '@celum/core/configuration';
import { TranslationHelper } from '@celum/ng2base/translation';

import { AUTH_CONTEXT, NamedAuthToken, TokenRequestDto } from './model/auth-token.model';
import { AuthService } from './services/auth.service';
import { ServiceAccessTokenProvider } from './services/service-access-token-provider.service';
import { getAuthTokens, getB2CConfigs, isB2COrSaccRequest, retrieveApiConfig } from './utils/auth-token-util';

export interface ServiceTokenInterceptorConfig<T> {
  /**
   * Returns the interceptor configuration. This is called before each request, so keep it as lightweight as possible
   */
  getInterceptorConfiguration(): T;
}

export interface AuthInterceptorConfig {
  /**
   * Configuration per url that should be intercepted
   */
  apiConfigurations: ApiConfiguration[];
  /**
   * Whether to ignore 403 errors during authentication (forcing the application to deal with it instead)
   */
  passThrough403Error?: boolean;
}

export type TokenEndpointConfiguration = {
  /**
   * Request for either a b2cToken or the specific dto that SACC requires to acquire the service access token
   */
  serviceTokenRequestDto: TokenRequestDto;

  /**
   * The header param name of the token. If set it will be used for providing the token as header param. The recommendation is to use kebap case with dashes
   * and uppercased letters, prefixed with 'X-' for the header param token name, e.g. X-Cloud-Token.
   *
   * Defaults to 'Authorization: Bearer <token>' for header params.
   */
  tokenHeaderParamName?: string;

  /**
   * The query param name of the token. If set it will be used for providing the token as query param. The recommendation is to use camel case for the
   * query param token name, e.g. cloudToken.
   *
   * Defaults to 'token' (e.g. 'request?token=') for query params.
   */
  tokenQueryParamName?: string;
  /**
   * The endpoint from which the token will be fetched. Defaults to ${CelumPropertiesProvider.properties.authentication.saccUrl}/auth/token.
   */
  tokenEndpoint?: string;

  /**
   * If set, a token will be returned from the authentication framework if it's available and not expired, but not refetched from token providers
   * (msal or custom token endpoints).
   *
   * In case of custom tokens that expired, the subsequent api call will return a 403 error instead of invoking the custom token endpoint again.
   * In case of B2C tokens, the redirect login flow won't be triggered (a token refresh might still be attempted for expired tokens).
   */
  noRefetch?: boolean;
};

export interface ApiConfiguration {
  /**
   * Unique identifier for the API configuration.
   */
  id?: string;
  /**
   * Interceptor will attach the provided dto to http requests starting with these urls
   */
  apiUrls: string[];
  /**
   * TenantId (a.k.a. organizationId) required for some backend calls. Will be attached as 'X-Tenant'-header
   */
  tenantIdHeader?: string;
  /**
   * List of token endpoints config that will be used to acquire and apply the custom tokens.
   */
  tokenEndpointConfigs?: TokenEndpointConfiguration[];
  /**
   * Optional list of scopes that will be used instead of the default scope
   */
  scopes?: string[];
}

export const SERVICE_TOKEN_INTERCEPTOR_CONFIG = new InjectionToken<ServiceTokenInterceptorConfig<AuthInterceptorConfig>>('Service Token Interceptor Config');

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly ERROR_KEY = 'errorKey';

  constructor(
    protected authService: AuthService,
    protected router: Router,
    @Inject(DOCUMENT) private document: Document,
    private serviceAccessTokenProvider: ServiceAccessTokenProvider,
    private translation: TranslationHelper,
    @Inject(SERVICE_TOKEN_INTERCEPTOR_CONFIG) private interceptorConfig: ServiceTokenInterceptorConfig<AuthInterceptorConfig>
  ) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.getB2CToken(req).pipe(
      switchMap(token => this.cloneHttpRequest(req, token)),
      switchMap(clone => next.handle(clone)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && error.headers.get(this.ERROR_KEY) === 'token-expired') {
          this.document.location.reload();
          return EMPTY;
        } else if (
          !this.interceptorConfig.getInterceptorConfiguration().passThrough403Error &&
          error.status === 403 &&
          req.url.startsWith(CelumPropertiesProvider.properties.authentication.saccUrl)
        ) {
          // If a request to fetch something from SACC leads to a 403, we don't have the permission/privilege, so redirect to the fallback page
          // Using document.location here, because for some reason the router doesn't navigate anywhere from here
          this.document.location.replace(CelumPropertiesProvider.properties.authentication.applicationFallbackPagesPerLanguage[this.translation.locale]);
          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Clone the http request and add tokens automatically
   * @param req - The request ot clone
   * @param b2CToken - B2C token for SACC calls
   * @private
   */
  private cloneHttpRequest(req: HttpRequest<any>, b2CToken?: string): Observable<HttpRequest<any>> {
    // Get the injected config and check if it maps to the current request
    const apiConfig = retrieveApiConfig(this.interceptorConfig, req.url);

    if (!b2CToken && !apiConfig) {
      return of(req);
    }

    // Attach B2C token automatically to calls to SACC
    if (req.url.includes(CelumPropertiesProvider.properties.authentication.saccUrl) && b2CToken) {
      return of(req.clone({ setHeaders: { Authorization: `Bearer ${b2CToken}` } }));
    }

    // get all necessary tokens for the intercepted url
    if (apiConfig) {
      // some token endpoints require additional context to issue tokens
      const endpointsWithContexts = req.context.get(AUTH_CONTEXT);

      return getAuthTokens(req.url, this.interceptorConfig, this.serviceAccessTokenProvider, { endpointsWithContexts }).pipe(
        map(token => (token ? req.clone(this.setAuthHeaders(apiConfig, token)) : req))
      );
    }

    return of(req);
  }

  private setAuthHeaders(apiConfig: ApiConfiguration, namedTokens: NamedAuthToken[]): { setHeaders?: { [name: string]: string } } {
    const setHeaders: Record<string, string> = {};

    namedTokens.forEach(namedToken => {
      const headerParam = this.getHeaderParam(apiConfig, namedToken);
      if (headerParam) {
        setHeaders[headerParam] = namedToken.token;
      } else {
        setHeaders.Authorization = `Bearer ${namedToken.token}`;
      }
    });

    if (apiConfig.tenantIdHeader) {
      setHeaders['X-Tenant'] = apiConfig.tenantIdHeader;
    }

    return { setHeaders };
  }

  private getB2CToken(req: HttpRequest<any>): Observable<string> {
    const url = req.url;

    if (!url.startsWith('http')) {
      return of(null);
    }

    const apiConfig = retrieveApiConfig(this.interceptorConfig, url);

    // trigger msal authentication only for requests that require it
    const isB2CRequest = isB2COrSaccRequest(apiConfig?.tokenEndpointConfigs, url);

    if (!isB2CRequest) {
      return of(null);
    }

    const noRefetch = getB2CConfigs(apiConfig?.tokenEndpointConfigs, url)?.every(config => config.noRefetch);

    if (!this.authService.isB2CTokenStillValid() && noRefetch) {
      return of(null);
    }

    // TODO: Switch to accessToken with this ticket: https://celum.atlassian.net/browse/SACC-5319
    return this.authService.getAuthResult().pipe(map(result => result?.idToken ?? ''));
  }

  /**
   * Get the header param name for the given token
   *
   * @param apiConfig the api configuration
   * @param token the token for which we want to get the header param name
   */
  private getHeaderParam(apiConfig: ApiConfiguration, token: NamedAuthToken): string {
    // it's fine to rely on the first found tokenEndpointConfig as using the same tokenQueryParamName for multiple endpoints of an apiConfig is not supported
    return apiConfig.tokenEndpointConfigs.find(config => config.tokenQueryParamName === token.name)?.tokenHeaderParamName;
  }
}
